import React from 'react'
import styled, { css } from 'styled-components'
import KEYBOARD_SHORTCUTS, { TShortcutName } from '../../../constants/shortcuts'
import { Border, Colors, Shadows, Spacing, Typography } from '../../../styles'
import { TIconColor, TTextColor } from '../../../styles/colors'
import Tip, { TTooltipSide } from '../../radix/Tip'
import { Icon, TIconType } from '../Icon'
import NoStyleButton from './NoStyleButton'

type TButtonType = 'primary' | 'secondary' | 'destructive' | 'control' | 'icon'

const PrimaryButtonStyles = css<GTButtonProps>`
    ${Typography.title.small};
    padding: ${Spacing._8} ${Spacing._16};
    color: ${Colors.control.primary.label};
    background-color: ${Colors.control.primary.bg};
    border: ${Border.stroke.medium} solid ${Colors.control.primary.stroke};
    box-shadow: ${Shadows.button.default};
    &:active:enabled,
    &:focus:enabled,
    &[data-state='open'] {
        background-color: ${Colors.control.primary.highlight};
        outline: ${Border.stroke.large} solid ${Colors.control.primary.stroke};
        outline-offset: -${Border.stroke.large};
    }
    &:hover:enabled {
        background-color: ${Colors.control.primary.hover};
        box-shadow: ${Shadows.button.hover};
    }
`
const SecondaryButtonStyles = css<GTButtonProps>`
    ${Typography.title.small};
    padding: ${Spacing._8} ${Spacing._16};
    color: ${Colors.control.secondary.label};
    background-color: ${Colors.control.secondary.bg};
    outline: ${Border.stroke.medium} solid ${Colors.control.secondary.stroke};
    outline-offset: -${Border.stroke.medium};
    &:active:enabled,
    &:focus:enabled,
    &[data-state='open'] {
        background-color: ${Colors.control.secondary.bg};
        outline: ${Border.stroke.large} solid ${Colors.control.secondary.highlight};
        outline-offset: -${Border.stroke.large};
    }
    &:hover:enabled {
        background-color: ${Colors.control.secondary.hover};
        outline: ${Border.stroke.medium} solid ${Colors.control.secondary.stroke};
        outline-offset: -${Border.stroke.medium};
    }
`
const DestructiveButtonStyles = css<GTButtonProps>`
    ${Typography.title.small};
    padding: ${Spacing._8} ${Spacing._16};
    color: ${Colors.control.destructive.label};
    background-color: ${Colors.control.destructive.bg};
    &:active:enabled,
    &:focus:enabled,
    &[data-state='open'] {
        background-color: ${Colors.control.destructive.highlight};
        outline: ${Border.stroke.medium} solid ${Colors.control.destructive.hover};
        outline-offset: -${Border.stroke.medium};
    }
    &:hover:enabled {
        background-color: ${Colors.control.destructive.hover};
    }
`
const ControlButtonStyles = css<GTButtonProps>`
    ${Typography.label.small};
    padding: ${Spacing._4} ${Spacing._8};
    color: ${Colors.text.muted};
    border: ${Border.stroke.medium} solid transparent;
    &:active:enabled,
    &:focus:enabled,
    &[data-state='open'] {
        color: ${Colors.text.ink};
        background-color: ${Colors.background.white};
        border-color: ${Colors.background.border};
    }
    &:hover:enabled {
        color: ${Colors.text.ink};
        background-color: ${Colors.background.sub};
        border-color: ${Colors.background.border};
    }
`
const IconButtonStyles = css<GTButtonProps>`
    ${Typography.label.small};
    padding: ${Spacing._4};
    color: ${Colors.text.muted};
    min-width: ${Spacing._28};
    min-height: ${Spacing._28};
    &:active:enabled,
    &:focus:enabled,
    &[data-state='open'] {
        color: ${Colors.text.ink};
        background-color: ${Colors.background.white};
        outline: ${Border.stroke.medium} solid ${Colors.background.border};
        outline-offset: -${Border.stroke.medium};
    }
    &:hover:enabled {
        color: ${Colors.text.ink};
        background-color: ${Colors.background.sub};
    }
`

const Button = styled(NoStyleButton)<GTButtonProps>`
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${Spacing._8};
    border-radius: ${Border.radius.medium};
    text-align: center;
    width: ${({ fitContent }) => (fitContent ? 'fit-content' : '100%')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    font-family: inherit;
    ${({ styleType }) => styleType === 'primary' && PrimaryButtonStyles};
    ${({ styleType }) => styleType === 'secondary' && SecondaryButtonStyles};
    ${({ styleType }) => styleType === 'destructive' && DestructiveButtonStyles};
    ${({ styleType }) => styleType === 'control' && ControlButtonStyles};
    ${({ styleType }) => styleType === 'icon' && IconButtonStyles};
    ${(props) => props.textColor && `color: ${Colors.text[props.textColor]} !important;`}
    &:disabled {
        opacity: 0.5;
        ${({ overrideDisabledStyle }) => overrideDisabledStyle && 'opacity: 1;'}
        cursor: default;
    }
`
const MarginLeftAuto = styled.div`
    margin-left: auto;
`
export interface GTButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
    // style
    styleType: TButtonType
    // text
    value?: React.ReactNode
    textColor?: TTextColor
    // left icon
    icon?: TIconType
    iconColor?: TIconColor
    iconColorHex?: string
    // right icon
    rightIcon?: TIconType
    rightIconColor?: TIconColor
    rightIconColorHex?: string
    // tooltip
    shortcutName?: TShortcutName
    overrideShortcut?: string
    overrideShortcutLabel?: string
    tooltipText?: string
    tooltipSide?: TTooltipSide
    // misc
    fitContent?: boolean
    overrideDisabledStyle?: boolean
}
// An icon-only button carries no text, so its tooltip is the only thing naming it — and a tooltip is
// a hover affordance that touch devices never show and screen readers only announce once it opens.
// Reuse whichever label the tooltip would have shown as the button's accessible name.
const getIconLabel = ({ styleType, tooltipText, overrideShortcutLabel, shortcutName }: GTButtonProps) => {
    if (styleType !== 'icon') return undefined
    if (tooltipText) return tooltipText
    return overrideShortcutLabel ?? (shortcutName ? KEYBOARD_SHORTCUTS[shortcutName].label : undefined)
}

const GTButton = React.forwardRef((props: GTButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const { value, fitContent, ...rest } = props
    const button = (
        <Button fitContent={fitContent ?? true} aria-label={getIconLabel(props)} ref={ref} {...rest}>
            {props.icon && <Icon icon={props.icon} color={props.iconColor} colorHex={props.iconColorHex} />}
            {props.styleType !== 'icon' ? value : ''}
            {props.rightIcon && props.styleType !== 'icon' && (
                <MarginLeftAuto>
                    <Icon icon={props.rightIcon} color={props.rightIconColor} colorHex={props.rightIconColorHex} />
                </MarginLeftAuto>
            )}
        </Button>
    )
    if (props.tooltipText || props.shortcutName) {
        return (
            <Tip
                content={props.tooltipText}
                shortcutName={props.shortcutName}
                side={props.tooltipSide}
                overrideShortcut={props.overrideShortcut}
                overrideShortcutLabel={props.overrideShortcutLabel}
            >
                {button}
            </Tip>
        )
    }
    return button
})

export default GTButton
